import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
	BOQ_ADD_ELEMENT_OPTIONS,
	ELEMENT_TYPES,
} from "../constants/boqElementTypes";

/**
 * Shared "Add" dropdown for BoQ: element types with submenu for Additional text (at beginning / at end).
 * Subgroup is disabled when canAddSubgroup is false (no structural row is selected).
 * Smart placement is handled by the onAddElement callback based on the currently selected row type.
 */
const BoQAddDropdown = ({
	onAddElement,
	canAddSubgroup = false,
	className,
	toggleVariant = "primary",
	toggleSize,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const [additionalTextSubmenuOpen, setAdditionalTextSubmenuOpen] = useState(false);

	const isOptionDisabled = (type) => {
		if (type === ELEMENT_TYPES.SUBGROUP) {
			return !canAddSubgroup;
		}
		return false;
	};

	const handleOptionClick = (type, position) => {
		if (disabled) return;
		if (type === ELEMENT_TYPES.SUBGROUP && isOptionDisabled(type)) return;
		onAddElement?.(type, position);
	};

	return (
		<Dropdown className={className}>
			<Dropdown.Toggle variant={toggleVariant} size={toggleSize} disabled={disabled}>
				<FontAwesomeIcon icon={faPlus} className="me-1" />
				{t("boq.toolbar.add")}
			</Dropdown.Toggle>
			<Dropdown.Menu>
				{BOQ_ADD_ELEMENT_OPTIONS.map((opt) =>
					opt.type === ELEMENT_TYPES.ADDITIONAL_TEXT ? (
						<Dropdown
							key={opt.type}
							show={additionalTextSubmenuOpen}
							onToggle={setAdditionalTextSubmenuOpen}
							drop="end"
							align="start"
							className="boq-add-additional-text-submenu"
						>
							<Dropdown.Toggle
								as="div"
								className="dropdown-item dropdown-item-submenu-trigger"
								onMouseEnter={() => setAdditionalTextSubmenuOpen(true)}
								onClick={(e) => e.preventDefault()}
							>
								<span className="boq-option-swatch boq-option-swatch--additional-text" />
								{t(opt.labelKey)}
							</Dropdown.Toggle>
							<Dropdown.Menu
								onMouseLeave={() => setAdditionalTextSubmenuOpen(false)}
							>
								<Dropdown.Item
									onClick={() => {
										setAdditionalTextSubmenuOpen(false);
										handleOptionClick(ELEMENT_TYPES.ADDITIONAL_TEXT, "start");
									}}
								>
									<span className="boq-option-swatch boq-option-swatch--additional-text" />
									{t("boq.add.at-beginning")}
								</Dropdown.Item>
								<Dropdown.Item
									onClick={() => {
										setAdditionalTextSubmenuOpen(false);
										handleOptionClick(ELEMENT_TYPES.ADDITIONAL_TEXT, "end");
									}}
								>
									<span className="boq-option-swatch boq-option-swatch--additional-text" />
									{t("boq.add.at-end")}
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					) : (
						<Dropdown.Item
							key={opt.type}
							disabled={isOptionDisabled(opt.type)}
							title={
								opt.type === ELEMENT_TYPES.SUBGROUP && !canAddSubgroup
									? t("boq.add.subgroup-disabled-hint")
									: undefined
							}
							onClick={() => handleOptionClick(opt.type)}
						>
							<span className={`boq-option-swatch boq-option-swatch--${opt.type.replace(/_/g, "-")}`} />
							{t(opt.labelKey)}
						</Dropdown.Item>
					),
				)}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export default BoQAddDropdown;
